import { useCallback } from 'react'

import { isInputConfig } from '../../../../application/formBuilder/config'
import { useProposal } from '../../../../application/product/proposal/ProposalProvider'
import {
  Data,
  getCurrentQuote,
  getInsuredProfile,
  PolicyChangeType,
  Profile,
  ProfileDisclosureChange,
  ProposalAdjustmentRequest,
} from '../../../../domain'
import { useAdjustments } from '../../../../state'
import {
  ChangeType,
  FormConfig,
  ProfileChangeConfig,
} from '../../../../state/configuration'
import { hasUpdates, toUpdates } from '../../helpers'

const toProfileReducer =
  (existingData: Data, data: Data, { profileCode }: Profile) =>
  (
    proposalAdjustment: ProposalAdjustmentRequest,
    field: string,
    adjustment: ProfileChangeConfig,
  ): ProposalAdjustmentRequest => {
    const updates = toUpdates(existingData, data)

    const changes: ProfileDisclosureChange[] = updates(field, adjustment).map(
      (update) => ({
        changeType: PolicyChangeType.PROFILE_DISCLOSURE_CHANGE,
        profileCode,
        update,
      }),
    )

    return {
      ...proposalAdjustment,
      changes: [...proposalAdjustment.changes, ...changes],
    }
  }

export const useAdditionalProfileProposalAdjustmentRequest = (
  form: FormConfig,
  applyRating: boolean,
  profile: Profile,
) => {
  const { getAdjustment } = useAdjustments()
  const { proposal } = useProposal()

  return useCallback(
    (existingData: Data, updatedData: Data): ProposalAdjustmentRequest => {
      const quote = getCurrentQuote(proposal)

      const profileReducer = toProfileReducer(existingData, updatedData, profile)

      return form
        .filter(isInputConfig)
        .filter(hasUpdates(existingData, updatedData))
        .reduce(
          (proposalAdjustment: ProposalAdjustmentRequest, input) => {
            const adjustment = getAdjustment(input.name)

            switch (adjustment?.changeType) {
              case ChangeType.PROFILE_DISCLOSURE:
                return profileReducer(proposalAdjustment, input.name, adjustment)
              default:
                throw new Error(
                  `Adjustment ${adjustment.changeType} not supported by this`,
                )
            }
          },
          {
            applyRating,
            changes: !getInsuredProfile(quote, profile.profileCode)
              ? [
                  {
                    changeType: PolicyChangeType.ADDITIONAL_PROFILE_CREATE_CHANGE,
                    profileCode: profile.profileCode,
                  },
                ]
              : [],
          },
        )
    },
    [form, applyRating, profile, proposal, getAdjustment],
  )
}
